'use client';

import { CSSProperties, FC, ReactNode } from 'react';
import type { ImageField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';

type Props = {
  sliceType: string;
  variation: string;
  removeUpperMargin: boolean;
  /** Optional counterpart to `removeUpperMargin` — drops the section's bottom
   * padding so the callout sits flush against the next slice. Defaults to
   * false (keeps the existing `pb-10 md:pb-[70px]` spacing). */
  removeLowerMargin?: boolean;
  backgroundImage: ImageField;
  children: ReactNode;
};

// Two-layer gradient in the Display P3 gamut for a richer green on
// wide-gamut screens. Base runs left → right through the five stops; a
// radial from the top-right corner mixes in the two accent stops so that
// corner doesn't get flattened to the linear's own endpoint. sRGB browsers
// gracefully fall back to the closest in-gamut approximation.
const GRADIENT_BACKGROUND: CSSProperties = {
  background: [
    'radial-gradient(120% 90% at 100% 0%,',
    '  color(display-p3 0.122 0.301 0.268) 0%,',
    '  color(display-p3 0.117 0.290 0.246) 30%,',
    '  transparent 70%),',
    'linear-gradient(to right,',
    '  color(display-p3 0.255 0.569 0.347) 0%,',
    '  color(display-p3 0.162 0.383 0.264) 50%,',
    '  color(display-p3 0.066 0.189 0.155) 75%,',
    '  color(display-p3 0.041 0.143 0.137) 90%,',
    '  color(display-p3 0.047 0.151 0.144) 100%)',
  ].join(' '),
};

// Outer chrome (section padding, rounded gradient/photo card) reused by every
// variation so each variant only owns its inner two-column layout.
export const CalloutCardShell: FC<Props> = ({
  sliceType,
  variation,
  removeUpperMargin,
  removeLowerMargin = false,
  backgroundImage,
  children,
}) => {
  const hasBackgroundImage = isFilled.image(backgroundImage);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      className={`bg-white px-5 md:px-[30px] ${
        removeUpperMargin ? 'pt-0' : 'pt-10 md:pt-[70px]'
      } ${removeLowerMargin ? 'pb-0' : 'pb-10 md:pb-[70px]'}`}
    >
      <div
        className="relative mx-auto w-full max-w-[1280px] overflow-hidden rounded-[10px] p-[15px]"
        style={hasBackgroundImage ? undefined : GRADIENT_BACKGROUND}
      >
        {hasBackgroundImage && (
          <>
            <PrismicNextImage
              field={backgroundImage}
              fill
              sizes="100vw"
              className="absolute inset-0 -z-10 h-full w-full object-cover"
              fallbackAlt=""
            />
            <div className="absolute inset-0 -z-10 bg-black/35" />
          </>
        )}
        {children}
      </div>
    </section>
  );
};
